import * as React from 'react';
import { CardModelV2, TablePlayerModelV2 } from 'shared';
import { LAYOUT } from '../../../ui/layout';
import { zVar } from '../../../ui/theme';
import { Avatar } from '../../../ui/Avatar';
import { Button } from '../../../ui/Button';
import { Hand } from '../../../ui/Hand';
import { useMediaQuery } from '../../../ui/useMediaQuery';
import { SeatDescriptor } from './seat-layout';

// On touch devices the whole board scales down, leaving the local hand's cards
// small and hard to tap accurately, so enlarge just the local face-up hand.
const LOCAL_HAND_TOUCH_SCALE = 1.4;

/*
 * Seat — one player position on the table. Places an Avatar and a Hand at the
 * coordinates from a SeatDescriptor (computed by seat-layout.ts). The local seat
 * shows a face-up interactive hand; all others show a face-down fan.
 */

interface SeatProps {
  seat: SeatDescriptor;
  player?: TablePlayerModelV2;
  /** Local player's face-up cards (only for the local seat). */
  localHand?: CardModelV2[];
  /** True when the local player may click a card to play it. */
  canPlay?: boolean;
  onPlay?: (cardId: string) => void;
  /** Local hand card ids to flag as unplayable (red outline). */
  illegalCardIds?: Set<string>;
  /** Card the local player just played: animates out of the fan. */
  playingCardId?: string;
  /** This seat is taking the completed trick. */
  isCollecting?: boolean;
  /** When set on an empty seat (pre-game), clicking takes that seat/team. */
  onTakeSeat?: () => void;
}

/** Absolutely-position (and optionally rotate) an element on its center. */
const placed = (x: number, y: number, rotationDeg = 0): React.CSSProperties => ({
  position: 'absolute',
  left: x,
  top: y,
  transform: `translate(-50%, -50%) rotate(${rotationDeg}deg)`,
  zIndex: zVar('z-seats'),
});

export const Seat: React.FC<SeatProps> = ({
  seat,
  player,
  localHand,
  canPlay,
  onPlay,
  illegalCardIds,
  playingCardId,
  isCollecting,
  onTakeSeat,
}) => {
  const namePlacement = seat.avatar.y < LAYOUT.table.cy ? 'above' : 'below';
  // Bigger, easier-to-tap cards for the local hand on touch devices.
  const isTouch = useMediaQuery('(pointer: coarse)');
  const localCardScale = isTouch ? LOCAL_HAND_TOUCH_SCALE : 1;

  // Empty, takeable seat (pre-game): show a "Sit here" button in its place.
  if (!player && onTakeSeat) {
    return (
      <div style={placed(seat.avatar.x, seat.avatar.y)}>
        <Button variant="secondary" size="sm" onClick={onTakeSeat}>
          Sit here
        </Button>
      </div>
    );
  }

  return (
    <>
      <div style={placed(seat.avatar.x, seat.avatar.y)}>
        <Avatar
          size={LAYOUT.avatar}
          name={player?.name}
          teamIndex={player?.teamIndex ?? seat.teamIndex}
          isActive={player?.isCurrentTurn}
          isCollecting={isCollecting}
          isComputer={player?.isComputer}
          isVacant={player?.isVacant}
          namePlacement={namePlacement}
        />
      </div>

      {player && (
        <div style={placed(seat.hand.x, seat.hand.y, seat.hand.rotationDeg)}>
          {seat.isLocal ? (
            <Hand
              cardW={LAYOUT.card.w * localCardScale}
              cardH={LAYOUT.card.h * localCardScale}
              fanSpreadDeg={LAYOUT.fan.spreadDeg}
              overlap={LAYOUT.fan.overlap}
              cards={localHand}
              isTurn={canPlay}
              onPlay={onPlay}
              illegalCardIds={illegalCardIds}
              playingCardId={playingCardId}
            />
          ) : (
            <Hand
              faceDown
              cardW={LAYOUT.faceDown.w}
              cardH={LAYOUT.faceDown.h}
              fanSpreadDeg={LAYOUT.fan.spreadDeg}
              overlap={LAYOUT.fan.overlap}
              count={player.numberOfCards}
            />
          )}
        </div>
      )}
    </>
  );
};

export default Seat;
